/**
 * Material color ROLES that drive the app chrome, and how they map onto the
 * existing CSS variables (`packages/ui/src/styles.css`). The app's Tailwind
 * config reads `hsl(var(--background))` etc., so the runtime injector writes
 * `H S% L%` triplets (see {@link hexToHslTriplet}) — no Tailwind change needed.
 *
 * Every role name here matches a method on Material's `MaterialDynamicColors`
 * (so generation can call `mdc[role]()` — see `generate.ts`).
 */
export const ROLE_NAMES = [
  "primary", "onPrimary", "primaryContainer", "onPrimaryContainer",
  "secondary", "onSecondary", "secondaryContainer", "onSecondaryContainer",
  "tertiary", "onTertiary", "tertiaryContainer", "onTertiaryContainer",
  "error", "onError", "errorContainer", "onErrorContainer",
  "background", "onBackground",
  "surface", "onSurface", "surfaceVariant", "onSurfaceVariant",
  "surfaceContainerLowest", "surfaceContainerLow", "surfaceContainer",
  "surfaceContainerHigh", "surfaceContainerHighest",
  "outline", "outlineVariant",
  "inverseSurface", "inverseOnSurface", "inversePrimary",
  "shadow", "scrim",
] as const;

export type RoleName = (typeof ROLE_NAMES)[number];

export type RoleColors = Record<RoleName, string>;

/** Role → app CSS variable name. A role may feed several vars; vars not listed
 *  here are derived from these (the injector writes exactly these var names). */
export const ROLE_TO_CSSVAR: Partial<Record<RoleName, readonly string[]>> = {
  surface: ["--background"],
  onSurface: ["--foreground", "--card-foreground", "--popover-foreground"],
  surfaceContainerLow: ["--card"],
  surfaceContainer: ["--popover"],
  surfaceContainerHighest: ["--muted"],
  onSurfaceVariant: ["--muted-foreground"],
  primary: ["--primary", "--ring"],
  onPrimary: ["--primary-foreground"],
  secondaryContainer: ["--secondary", "--accent"],
  onSecondaryContainer: ["--secondary-foreground", "--accent-foreground"],
  error: ["--destructive"],
  onError: ["--destructive-foreground"],
  outlineVariant: ["--border", "--input"],
};

/** Convert a `#rrggbb` hex to a Tailwind-ready `"H S% L%"` HSL triplet. */
export function hexToHslTriplet(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1]!, 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
